/**
 * Send user to suite shell login screen when the SPA has no session.
 *
 * Uses `/login` instead of `/` because nginx `location = /` 302s to the primary
 * SPA (e.g. `/remote/admin/`), which would loop back here. `/login` falls
 * through the nginx catch-all to the shell, whose `Root` renders `<LoginScreen />`
 * for any path when unauthenticated.
 *
 * `returnTo` lets satellite SPAs round-trip the user back to where they were
 * after auth (e.g. `/remote/video/<room>/`). Only same-origin paths are honored
 * (must start with `/` and not `//`); cross-origin URLs are silently dropped.
 */
export function redirectToShellHome(returnTo?: string): void {
  if (typeof window === "undefined") return;
  const next = sameOriginPath(returnTo);
  const target = next ? `/login?next=${encodeURIComponent(next)}` : "/login";
  window.location.assign(target);
}

function sameOriginPath(returnTo: string | undefined): string | null {
  if (!returnTo) return null;
  try {
    const url = new URL(returnTo, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    const path = `${url.pathname}${url.search}${url.hash}`;
    if (!path.startsWith("/") || path.startsWith("//")) return null;
    return path;
  } catch {
    return null;
  }
}
